<template>
  <div :class="[`checkout-product`, { loading }]">
    <ui-loader v-if="loading" size="48" />

    <div class="title">
      <div class="name">
        {{ model.name }}
        <p>{{ model.code }}</p>
      </div>

      <template v-if="editable">
        <div v-if="editable" class="icon-bin" @click="removeConfirm = true">
          <f-sprite-icon :icon-link="$options.icons.IconBin.id" />
        </div>
        <modal-confirm
          :visible="removeConfirm"
          :title="`Удалить товар из корзины?`"
          :buttons="[
            {
              text: 'Удалить',
              action: remove,
            },
            {
              text: 'Нет, оставить',
              flat: true,
            }
          ]"
          @on-close="() => removeConfirm = false"
        />
      </template>
    </div>

    <a :href="productURL" class="thumb" target="_blank">
      <media :media="media" width="192" />
    </a>

    <ul class="info">
      <li>
        <span>Цвет</span> {{ color }}
      </li>

      <li>
        <span>Размер</span> {{ size }}
      </li>

      <li>
        <span>Цена</span>
        <div class="price-rub">
          {{ price.discountPrice || price.basePrice | price }}
        </div>
        <div
          v-if="isDiscount"
          class="old-price"
        >
          {{ price.basePrice | price }}
        </div>
      </li>
      <!-- Скидки в корзине -->
      <li v-if="discounts.promotion" class="checkout-product__promotions">
        <div class="checkout-product__promotions-title">
          {{ discounts.promotion.length | decl('Акци', ['я', 'и', 'й']) }}
        </div>
        <div class="checkout-product__promotions-inner-wrap">
          <div
            class="checkout-product__promotion"
            v-for="(promotion, index) in discounts.promotion"
            :key="index"
          >
            <div class="checkout-product__promotion-name">
              {{ promotion.name }}
            </div>
            <div class="price price-rub discount">
              - {{ promotion.value | price }}
            </div>
          </div>
        </div>
      </li>

      <li v-if="discounts.promocode" class="checkout-product__promotions">
        <vue-client-only>
          <div class="checkout-product__promotions-title">
            <template v-if="adaptive.isMobile">
              Промо
            </template>
            <template v-else>
              Промокод
            </template>
          </div>
        </vue-client-only>
        <div class="checkout-product__promotions-inner-wrap">
          <div
            class="checkout-product__promotion"
            v-for="(promotion, index) in discounts.promocode"
            :key="index"
          >
            <div class="checkout-product__promotion-name">
              {{ promotion.name }}
            </div>
            <div class="price price-rub discount">
              - {{ promotion.value | price }}
            </div>
          </div>
        </div>
      </li>
      <!-- В карточке заказа -->
      <li v-if="!editable && Math.floor(discount.value)">
        <span>
          Скидка
        </span>
        <span
          :class="['price price-rub', { discount: Number(discount.value) }]"
        >
          - {{ discount.value | price }}
        </span>
      </li>

      <li v-if="promocode && Number(promocode.value)">
        <vue-client-only>
          <span>
            <template v-if="adaptive.isMobile">Промо</template>
            <template v-else>Промокод</template>
          </span> {{ promocode.name }}
        </vue-client-only>
        <div :class="['price price-rub', { discount: promocode.value }]">
          - {{ promocode.value | price }}
        </div>
      </li>
    </ul>

    <div class="utils">
      <div
        class="qty-info"
        v-if="!model.available"
      >
        Товар распродан
      </div>
      <div v-else class="select checkout-product__size">
        <ui-select
          :disabled="!editable"
          :value="product.quantity"
          :options="qtyOptions"
          @change="({name}) => changeQty(name)"
        />
      </div>

      <div class="buttons">
        <favorite-button
          :product-id="model.id"
          :product-code="model.code"
          :product-price="currentPrice"
          :quantity="product.quantity"
          :sku-id="product.skuId"
        />
        <f-sprite-icon
          v-if="editable"
          class="edit"
          :icon-link="$options.icons.IconEdit.id"
          @click.native="$emit('edit')"
        />

        <div :class="['price price-rub']">
          {{ product.totalPrice | price }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FavoriteButton from '@/components/favorite-button/favorite-button.vue';

import { mapActions } from 'vuex';
import VueClientOnly from 'vue-client-only';

/* Icons */
import IconBin from '@icons/bin.svg';
import IconEdit from '@icons/edit.svg'

export default {
  name: 'checkout-product',

  components: {
    FavoriteButton,
    VueClientOnly,
  },

  inject: ['adaptive'],

  icons: {
    IconBin,
    IconEdit
  },

  props: {
    product: {
      type: Object,
      default: () => {},
    },

    shippingGroupId: {
      type: String,
      default: ''
    },

    editable: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    qtyOptions: new Array(10).fill(null).map((v, i) => ({ name: i + 1 })),
    removeConfirm: false,
    loading: false,
  }),

  computed: {
    model() {
      return this.product.models?.[0] || {};
    },
    media() {
      return this.model.media?.[0] || {};
    },
    color() {
      return this.model.color?.name || '';
    },
    size() {
      return this.model.skus?.find(sku => sku.id === this.product.skuId)?.size;
    },
    price() {
      return this.model.price || {};
    },
    isDiscount() {
      return Number(this.model.price?.discountRate);
    },
    detailedDiscount() { // отображаем бОльшую скидку из приходящих
      let discount;
      let values = [];

      if (this.product.discounts.length) {
        this.product.discounts.forEach(p => {
          values.push(Math.floor(p.value));
        })
        values.sort((a, b) => b - a);

        discount = this.product.discounts.find(p => {
          return Math.floor(p.value) === values[0];
        })
      } else {
        discount = {name: '', value: 0}
      }

      return discount;
    },
    discounts() {
      if (!this.product.discounts?.length) {
        return {};
      }

      return this.product.discounts
        .map(d => {
          return {
            value: Math.floor(d.value),
            ...d,
          }
        })
        .reduce((acc, d) => {
          if (!acc[d.type]) {
            acc[d.type] = [];
          }

          acc[d.type].push(d);

          return acc;
        }, {})

    },
    discount() {
      return this.product.discounts.length ? this.product.discounts[0] : {name: '', value: 0};
    },
    promocode() {
      let promo;
      this.product.discounts.forEach(d => {
        if (d.type === 'promocode') {
          promo = {name: d.name, value: d.value}
        }
        promo = {name: '', value: 0}
      });
      return promo;
    },
    currentPrice() {
      return this.model.price?.discountPrice || this.model.price?.basePrice;
    },
    productURL() {
      return `${this.model.url}?sku-id=${this.product.skuId}`;
    },
  },

  methods: {
    ...mapActions({
      CHANGE_QTY: 'cart/changeQty',
      REMOVE: 'cart/remove'
    }),
    remove() {
      this.loading = true;
      this.removeConfirm = false;
      return this.REMOVE({lineIds: this.product.lineIds}).catch(() => {
        this.loading = false;
      })
    },

    async changeQty(qty) {
      this.loading = true;

      try {
        await this.CHANGE_QTY({
          lineIds: this.product.lineIds,
          quantity: this.product.quantity,
          updatedQuantity: qty,
          productId: this.product.id,
          skuId: this.product.skuId,
          shippingGroupId: this.shippingGroupId
        })
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style lang="scss">
.checkout-product {
  position: relative;
  display: grid;
  /* stylelint-disable-next-line */
  grid-template-areas:
    "title title"
    "img info"
    "utils utils";
  grid-template-rows: auto 129px auto;
  grid-template-columns: 80px auto;
  padding: 20px $containerGapMobile;
  border-bottom: 1px solid $gray_lu;

  .title {
    grid-area: title;
    display: flex;

    .name {
      font-weight: 600;
      font-size: 12px;
      line-height: 20px;
      margin: 1px 10px 12px 0;

      p {
        margin-top: 4px;
        font-weight: normal;
        font-size: 10px;
        line-height: 12px;
        color: $gray;
      }
    }

    .icon-bin {
      position: relative;
      margin-left: auto;
      color: $gray_l;
      width: 22px;
      height: 22px;
      cursor: pointer;
    }

    .remove-confirm {
      position: absolute;
      right: 0;
      width: 320px;
      .ui-notification {
        margin: 5px 0;
      }
    }
  }

  .thumb {
    grid-area: img;
    height: 110px;
    width: 80px;
    background: $gray_lu;
    margin-right: 12px;
    display: block;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .info {
    grid-area: info;
    padding-left: 12px;
    font-size: 12px;
    line-height: 16px;
  }

  li:first-child {
    margin-top: 0;
  }

  li {
    display: flex;
    align-items: baseline;
    margin-top: 8px;

    & > span,
    .checkout-product__promotions-title {
      display: block;
      min-width: 42px;
      margin-right: 8px;
      color: $gray;
    }

    .price {
      display: inline-flex;
      align-self: flex-end;
      justify-content: flex-end;
      min-width: 50px;
      font-weight: 600;
      font-size: 12px;
      color: $red;
      line-height: 16px;
      margin: 0 0 0 auto;
    }

    .old-price {
      color: $gray;
      margin-left: 5px;
      font-weight: normal;
      font-size: 10px;
      text-decoration: line-through;
    }
  }

  .checkout-product__promotions-inner-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .checkout-product__promotion {
    display: flex;
  }

  .utils {
    position: relative;
    display: flex;
    height: 36px;
    grid-area: utils;
    padding-left: 92px;
    align-items: center;

    .qty-info {
      font-family: Gilroy, sans-serif;
      font-size: 12px;
      line-height: 16px;
      font-weight: 600;
      color: $red;
      flex-shrink: 0;
      width: 80px;
      margin-left: -92px;
      margin-right: 13px;
      text-align: center;
    }

    .select {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 80px;

      .input {
        height: 40px;
      }
    }

    .favorite-button {
      width: 20px;
      height: 20px;

      svg {
        width: 20px;
        height: 20px;
      }

      &:not(.active) {
        svg {
          color: $gray_l;
        }
      }
    }

    .buttons {
      display: flex;
      width: 100%;
      margin-top: 16px;

      & > svg {
        width: 20px;
        height: 20px;
        color: $gray_l;
      }

      .edit {
        margin-left: 16px;
      }

      .price {
        display: flex;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;
        margin-left: auto;
      }
    }
  }

  &.loading {
    pointer-events: none;
    > *:not(.ui-loader) {
      opacity: .6;
    }
    > .ui-loader {
      z-index: 1;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: $blue;
    }
  }
}

@include breakpoint("tablet") {
  .checkout-product {
    /* stylelint-disable-next-line */
    grid-template-areas:
      "img title"
      "img info"
      "img utils";
    grid-template-columns: 164px auto;
    grid-template-rows: auto minmax(132px, auto) auto;
    padding: 32px 0;

    .thumb {
      width: 164px;
      height: 226px;
    }

    .title {
      .name {
        margin: 0 0 16px 0;
        padding-left: 24px;
        font-size: 16px;
        line-height: 24px;

        p {
          display: inline;
          font-size: 12px;
          margin: 0 0 0 8px;
        }
      }
    }

    .info,
    .utils {
      padding-left: 24px;
    }

    .info {
      font-size: 14px;
      line-height: 20px;
    }

    li {
      span,
      .checkout-product__promotions-title {
        min-width: 68px;
        margin-right: 16px;
      }

      .price {
        font-size: 14px;
        line-height: 20px;
      }

      .old-price {
        font-size: 12px;
        line-height: 16px;
      }
    }

    .utils {
      height: 40px;
      margin-top: 14px;

      .qty-info {
        font-size: 14px;
        line-height: 20px;
        margin-left: 0;
        width: auto;
      }

      .select {
        position: relative;
        min-width: 74px;
        width: 74px;
        margin-right: 19px;
      }

      .buttons {
        align-items: center;
        margin-top: 0;

        .price {
          font-size: 24px;
          line-height: 38px;
        }
      }
    }

    &__promotion {
      .price-rub {
        &::after {
          display: none;
        }

        &.discount {
          flex-shrink: 0;
          align-self: flex-start;
          min-width: 57px;
          margin-left: 16px;
        }
      }
    }
  }
}

@include breakpoint("desktop") {
  .checkout-product {
    .utils {
      svg {
        cursor: pointer;
      }
    }

    &__promotion {
      .price-rub {
        &::after {
          display: inline;
        }

        &.discount {
          flex-shrink: 0;
          align-self: flex-end;
          min-width: 57px;
          margin-left: auto;
        }
      }
    }
  }
}
</style>
